/**
 * The page's tabs. The first three are campaign lifecycle and Ops-facing; "client" is
 * the Client Management tab (MPN-1868), which is CSM-facing and organised by client
 * relationship instead. The backend routes rows to it through a `Lifecycle.CLIENT`
 * sentinel that no campaign's lifecycle ever resolves to -- see `scope.Lifecycle`.
 */
export type OpsTab = "onboarding" | "live" | "ended" | "client";

/**
 * Why a table's output is provisional. Three kinds because they route to different
 * people: a decision waits on Ops or Product, schema waits on an engineer, and data
 * waits on someone filling in a field that already exists.
 */

/** What each kind is waiting on, for the page-level legend. */
/** Design system Badge tones. The backend emits these directly. */
export type BadgeTone = "neutral" | "brand" | "indigo" | "success" | "warning" | "danger" | "info";

/** A payload field is either a plain value or a toned one, per the mockup's decorate(). */
export interface TonedValue {
    tone: BadgeTone;
    t: string;
}

export type PayloadValue = string | number | null | TonedValue;

export function isToned(v: PayloadValue): v is TonedValue {
    return typeof v === "object" && v !== null && "tone" in v && "t" in v;
}

export interface OpsFilters {
    tab: OpsTab;
    opsOwnerIds: number[];
    organizationIds: number[];
    csmIds: number[];
    contractIds: number[];
    /** Onboarding only: "" | "this" | "later". */
    startWindow: string;
    /** Ended only: "" | "this" | "last". */
    endWindow: string;
}

/**
 * The date windows, per tab. Onboarding runs 14-21 days, so what matters is whether a
 * campaign starts inside this week; Ended uses CALENDAR months because Ops reconciles
 * revenue and margin by month and a rolling window would disagree with the margin
 * figures on this same page. The Live tab has neither.
 */
export const DATE_WINDOWS: Partial<Record<OpsTab, {
    label: string; allLabel: string; help: string; param: "startWindow" | "endWindow";
    options: { value: string; label: string }[];
}>> = {
    onboarding: {
        label: "Start Date",
        allLabel: "All start dates",
        help: "Filters every table on this tab. Onboarding runs 14\u201321 days.",
        param: "startWindow",
        options: [
            { value: "this", label: "Starting this week" },
            { value: "later", label: "Starting later" },
        ],
    },
    ended: {
        label: "End Date",
        allLabel: "All end dates",
        help: "Filters every table on this tab. Calendar months, so these agree with margin reporting.",
        param: "endWindow",
        options: [
            { value: "this", label: "Ended this month" },
            { value: "last", label: "Ended last month" },
        ],
    },
};

/** Who accepted a flag as fine, when, and why (MPN-2010). */
export interface OpsAcceptance {
    discrepancy_id: number;
    reason: string;
    accepted_by: string;
    accepted_at: string | null;
}

export interface OpsAlertRow {
    subject_type: string;
    subject_id: number;
    severity: string;
    payload: Record<string, PayloadValue>;
    launch_id: number | null;
    engagement_id: number | null;
    /** CREATE or REACH — decides which workflow tabs a deep link may target. */
    engagement_type: string | null;
    organization_id: number | null;
    contract_id: number | null;
    ops_owner_id: number | null;
    csm_id: number | null;
    /** Set only when the tile was asked for its accepted rows. */
    acceptance?: OpsAcceptance | null;
}

export interface OpsTile {
    tile_key: string;
    count: number;
    /** Rows in this tab before the filter bar. Equals `count` when nothing is filtered. */
    total: number;
    rows: OpsAlertRow[];
}

export interface OpsFilterOption {
    id: number;
    label: string;
    /** Depth in the organization tree, for indenting. 0 for every other filter. */
    depth?: number;
}

export interface OpsSummary {
    generated_at: string | null;
    status: string;
    /** Row count per lifecycle tab, so every status pill shows a count. */
    tab_totals: Record<OpsTab, number>;
    tiles: {
        tile_key: string;
        count: number;
        total: number;
        /** The rule as maths, in the `frac{a}{b}` notation the page typesets. */
        formula: string;
        /** The one-sentence caveat shown beneath the formula. */
        method: string;
        tiers: string[][];
        /** Whether a row can be accepted as fine (MPN-2010). */
        acceptable?: boolean;
    }[];
    ops_owners: OpsFilterOption[];
    organizations: OpsFilterOption[];
    csms: OpsFilterOption[];
    contracts: OpsFilterOption[];
}

/** Column rendering, taken from the mockup's own <td> styles. */
export type ColumnKind = "link" | "text" | "mono" | "muted" | "number" | "badge" | "progress";

export interface Column {
    key: string;
    label: string;
    kind: ColumnKind;
    /**
     * Whether the mockup sets `white-space: nowrap` on this cell. Per COLUMN, not per
     * kind: dates, statuses and file names hold one line, while Campaign and Ad Group
     * wrap. Blanket-applying nowrap to every text cell makes Ad Group claim all the width
     * it needs and squeezes Campaign into a four-line column.
     */
    nowrap?: boolean;
    /**
     * Whether the mockup right-aligns this column. Independent of `kind`, because the
     * mockup right-aligns plenty of BADGE cells too — Days Late, Days Flat, Reach %,
     * Margin, # Awaiting. Deriving alignment from the kind drops every one of those.
     */
    align?: "right";
}

/**
 * Which campaign page a table's rows link to. MPN-1840: "Link targets are campaign-scoped
 * and differ per table: Monitor, Schedule, Audience, or Ad Group. Each table ticket names
 * its own target." Monitor is the default for tables whose ticket names none — it is the
 * epic's stated purpose ("click-through into individual campaign Monitor pages").
 */
export type LinkTarget = "monitor" | "schedule" | "audience" | "adGroups" | "content";

export const LINK_TARGET_SLUG: Record<LinkTarget, string> = {
    monitor: "monitor",
    schedule: "schedule",
    audience: "audience",
    adGroups: "ad-groups",
    content: "content",
};

export interface TileSpec {
    /** Exact <h3> text from the mockup. */
    title: string;
    /** Exact tooltip copy from the mockup's info-circle. */
    tip?: string;
    /** Paragraph shown above the card, where the mockup has one. */
    note?: string;
    /** Unit for the "N Campaigns" count, from the mockup's units() map. */
    unit: string;
    /** Overrides the count label entirely, where the mockup does. */
    countLabel?: (n: number) => string;
    /**
     * Bootstrap Icons class for the empty state. An empty table is the healthy state
     * here, so the icon should picture WHAT IS ABSENT — a paused ad group, a calendar
     * with nothing on it — rather than a warning about it.
     */
    emptyIcon?: string;
    /** Which campaign page this table's rows link to; named by its own ticket. */
    linkTarget?: LinkTarget;
    /**
     * Renders full width. True when the mockup sets `grid-column:1/-1` on the card, when
     * the card's grid is a single `1fr` column, or when the card sits in no grid at all —
     * three different mechanisms with the same rendered result.
     */
    spanFull?: boolean;
    columns: Column[];
}

export const TILE_SPECS: Record<string, TileSpec> = {
    overdueFiles: {
        title: "Campaigns With Overdue Data Files",
        emptyIcon: "bi-file-earmark-x",
        // Interim rule pending MPN-1719, which records an expected cadence per client.
        // The Expected Cadence column went with it: there was nothing behind it but a
        // 7-day default standing in for 84 of 88 feeds (MPN-2074 item 1).
        tip: "Organizations whose newest data file is more than 4 days old",
        unit: "Campaign",
        linkTarget: "monitor",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "days", label: "Days Since Upload", kind: "text", nowrap: true, align: "right" },
            { key: "latest", label: "Latest Upload", kind: "text", nowrap: true },
        ],
    },
    adsScheduling: {
        title: "Ads Needing Scheduling",
        emptyIcon: "bi-calendar-week",
        tip: "Live campaigns only \u00b7 schedule has an empty date within the next 7 days \u00b7 excludes ad groups not yet on a network",
        unit: "Ad Group",
        linkTarget: "schedule",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "date", label: "First Empty Date", kind: "badge", nowrap: true },
        ],
    },
    audienceChanges: {
        title: "Conversion Changes",
        emptyIcon: "bi-activity",
        tip: "Unusual conversion activity — 100+ conversions on a single action in one day.",
        unit: "Action",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "action", label: "Action", kind: "text" },
            { key: "conversions", label: "Conversions (One Day)", kind: "badge", nowrap: true, align: "right" },
            { key: "date", label: "Date", kind: "text", nowrap: true },
        ],
    },
    conversionsFlat: {
        title: "Conversions At Zero / Flat 7+ Days",
        emptyIcon: "bi-graph-down",
        tip: "No conversion movement in the trailing 7 days",
        unit: "Campaign",
        linkTarget: "monitor",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "last", label: "Last Conversion", kind: "text", nowrap: true },
            { key: "days", label: "Days Flat", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    constituents: {
        title: "Recent Constituent File Changes",
        emptyIcon: "bi-file-earmark-text",
        tip: "Near-real-time missing/added constituents per recent file — added may push over contract limit; missing may need follow-up",
        unit: "File",
        spanFull: true,
        linkTarget: "monitor",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "file", label: "File Name", kind: "mono", nowrap: true },
            { key: "asOf", label: "As Of", kind: "text", nowrap: true },
            { key: "status", label: "File Status", kind: "badge", nowrap: true },
            { key: "added", label: "# Added", kind: "text", nowrap: true, align: "right" },
            { key: "removed", label: "# Removed", kind: "text", nowrap: true, align: "right" },
            { key: "total", label: "Total", kind: "text", nowrap: true, align: "right" },
            { key: "processed", label: "Processed On", kind: "muted", nowrap: true },
        ],
    },
    offboard: {
        title: "Upcoming Offboard Dates",
        emptyIcon: "bi-box-arrow-right",
        tip: "End date (driven by last schedule date) within 14 days",
        unit: "Campaign",
        spanFull: true,
        linkTarget: "monitor",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "end", label: "End Date", kind: "text", nowrap: true },
            { key: "status", label: "Status", kind: "badge", nowrap: true },
        ],
    },
    listSize: {
        title: "List Size vs. Contracted Max",
        emptyIcon: "bi-list-ol",
        unit: "Contract",
        spanFull: true,
        columns: [
            { key: "contract", label: "Contract", kind: "link" },
            { key: "client", label: "Organization", kind: "text" },
            { key: "campaigns", label: "Live Campaigns Rolled Up", kind: "text", nowrap: true, align: "right" },
            { key: "size", label: "Current Total Size", kind: "text", nowrap: true, align: "right" },
            { key: "max", label: "Contracted Max", kind: "text", nowrap: true, align: "right" },
            { key: "status", label: "Status", kind: "badge", nowrap: true },
        ],
    },
    scheduledNotServing: {
        title: "Scheduled but Not Currently Serving",
        emptyIcon: "bi-pause-circle",
        tip: "Ads that are scheduled to be running actively, but are not delivering \u00b7 excludes ad groups not yet on a network",
        unit: "GuidePost",
        linkTarget: "schedule",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "gp", label: "GuidePost", kind: "text", nowrap: true },
            { key: "status", label: "Network Status", kind: "badge", nowrap: true },
        ],
    },
    servingNotScheduled: {
        title: "Serving but Not Scheduled",
        emptyIcon: "bi-play-circle",
        tip: "Catches the reverse mismatch — live delivery without a matching schedule entry",
        unit: "GuidePost",
        linkTarget: "schedule",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "gp", label: "GuidePost", kind: "text", nowrap: true },
            { key: "status", label: "Network", kind: "badge" },
        ],
    },
    approval: {
        title: "GuidePosts Awaiting Client Approval",
        emptyIcon: "bi-check2-square",
        tip: "Count of approvals pending by campaign + ad group",
        unit: "GuidePost",
        linkTarget: "adGroups",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "n", label: "# Awaiting Client Approval", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    reworks: {
        title: "Overdue Reworks",
        emptyIcon: "bi-arrow-repeat",
        tip: "GuidePosts in 'Changes Requested' for 7+ days",
        unit: "Ad Group",
        linkTarget: "content",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "n", label: "# In Changes Requested", kind: "text", nowrap: true, align: "right" },
            { key: "days", label: "Days in Status", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    underdelivery: {
        title: "Campaigns Under-Delivering Impressions",
        emptyIcon: "bi-bar-chart",
        tip: "Avg. daily impressions since go-live below 2x target. Moved out of daily triage — not a daily-triage item, but useful for spotting list-quality or underspend issues.",
        unit: "Campaign",
        linkTarget: "monitor",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "start", label: "Start Date", kind: "text", nowrap: true },
            { key: "avg", label: "Avg Daily Impr.", kind: "text", nowrap: true, align: "right" },
            { key: "target", label: "Target", kind: "text", nowrap: true, align: "right" },
        ],
    },
    lowReach: {
        title: "Audiences With Low Reach",
        emptyIcon: "bi-broadcast",
        tip: "Currently only visible on the Monitor page — surfaced here as a flag",
        unit: "Audience",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "network", label: "Network", kind: "text", nowrap: true },
            // "Reach (%)", restoring the mockup's own label (MPN-2074 item 8). MPN-1852
            // called this Match Rate to stress that the denominator is network-matched
            // accounts rather than the contracted list — true of the denominator, wrong
            // about the figure: reach over matched is still a reach figure. The table
            // directly below now reports the actual match rate, and two tables cannot
            // both be called one.
            { key: "reach", label: "Reach (%)", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    lowMatchRate: {
        title: "Audiences With Low Match Rate",
        emptyIcon: "bi-people",
        tip: "The share of the people we sent that the network recognised — the same figure the Monitor page's Audience tab reports under Matched, By Network.",
        unit: "Audience",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "network", label: "Network", kind: "text", nowrap: true },
            { key: "rate", label: "Match Rate %", kind: "text", nowrap: true, align: "right" },
        ],
    },
    pfrBudget: {
        title: "Campaigns Approaching Budget",
        emptyIcon: "bi-wallet2",
        tip: "Filter: Contract.Billing Method = \"PFR\" AND Contract.Revenue Cap is nonzero. Rolled up to the contract-holding organization, where multiple campaigns share one contract-level budget.",
        unit: "Organization",
        spanFull: true,
        linkTarget: "monitor",
        columns: [
            { key: "client", label: "Organization", kind: "link" },
            { key: "campaigns", label: "Campaigns Rolled Up", kind: "text", nowrap: true, align: "right" },
            { key: "spend", label: "Revenue/Spend to Date", kind: "text", nowrap: true, align: "right" },
            { key: "cap", label: "Revenue Cap", kind: "text", nowrap: true, align: "right" },
            { key: "days", label: "Est. Days Remaining", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    overspend: {
        title: "Daily Spend vs. Target",
        emptyIcon: "bi-cash-stack",
        tip: "Subscription launches \u00b7 today's spend, target, and overall average side by side",
        unit: "Campaign",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "target", label: "Target Daily Spend", kind: "text", nowrap: true, align: "right" },
            { key: "today", label: "Daily Spend (Today)", kind: "badge", nowrap: true, align: "right" },
            { key: "avg", label: "Avg Daily Spend (Overall)", kind: "text", nowrap: true, align: "right" },
        ],
    },
    cpcCpm: {
        title: "CPM Outliers",
        emptyIcon: "bi-currency-dollar",
        tip: "Campaigns paying more per thousand impressions than the agreed line, and which channel is driving it",
        unit: "Campaign",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "cpm", label: "Blended CPM", kind: "text", nowrap: true, align: "right" },
            { key: "spend", label: "Spend (14d)", kind: "text", nowrap: true, align: "right" },
            { key: "channels", label: "Channels Over Guideline", kind: "badge" },
        ],
    },
    spendDropoff: {
        title: "Spend Drop-Off Alerts",
        emptyIcon: "bi-graph-down-arrow",
        tip: "Steady daily spend suddenly going to $0 without being manually turned off — catches a GuidePost silently failing to publish/traffic",
        unit: "Network",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "network", label: "Network", kind: "text" },
            { key: "avg", label: "Avg Daily Spend (7d)", kind: "text", nowrap: true, align: "right" },
            { key: "today", label: "Spend (Today)", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    creativeRefresh: {
        title: "Creative Refresh Flag",
        emptyIcon: "bi-images",
        tip: "Rotation age — days a GuidePost has been running/scheduled without new creative added.",
        unit: "Campaign",
        linkTarget: "monitor",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "gp", label: "GuidePost", kind: "text", nowrap: true },
            { key: "days", label: "Days Since Last New Creative", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    reviewCadence: {
        title: "Performance Review Cadence",
        emptyIcon: "bi-eye",
        tip: "Flags whether an Ops Manager has visited the Monitor page for this campaign in the last 14 days",
        unit: "Campaign",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "ownerInit", label: "Ops Owner", kind: "text", nowrap: true },
            { key: "last", label: "Last Monitor Page Visit", kind: "badge", nowrap: true },
        ],
    },
    keyDates: {
        title: "Upcoming Key Dates",
        emptyIcon: "bi-calendar-event",
        unit: "Date",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "kind", label: "Key Date", kind: "text", nowrap: true },
            { key: "date", label: "Date", kind: "text", nowrap: true },
        ],
    },
    liveCampaigns: {
        title: "Live Campaigns",
        emptyIcon: "bi-megaphone",
        tip: "Browsable/filterable list, not objective reporting \u00b7 the Organization shown is the one running the campaign",
        unit: "Campaign",
        spanFull: true,
        linkTarget: "monitor",
        columns: [
            { key: "org", label: "Organization", kind: "text" },
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "start", label: "Start Date", kind: "text", nowrap: true },
            { key: "end", label: "End Date", kind: "text", nowrap: true },
            { key: "days", label: "Days Live", kind: "text", nowrap: true, align: "right" },
        ],
    },
    marginByCampaign: {
        title: "Margin By Campaign",
        emptyIcon: "bi-percent",
        tip: "Sorted by margin, furthest below target first. The target is 87% at the portfolio level — performance is judged on whether the portfolio averages 87%, not on whether an individual campaign hits it. A single campaign below 87% only matters insofar as it pulls the portfolio average down.",
        unit: "Campaign",
        spanFull: true,
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "org", label: "Organization", kind: "text" },
            { key: "budget", label: "Budget Available (Period)", kind: "text", nowrap: true, align: "right" },
            { key: "spend", label: "Spend (Period)", kind: "text", nowrap: true, align: "right" },
            { key: "margin", label: "Margin", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    readiness: {
        title: "Campaigns Set Up, Not Yet Live",
        emptyIcon: "bi-rocket-takeoff",
        tip: "Shows all campaigns created but not yet live. Full detail split into Internal Setup Readiness and Client-Pending Items.",
        unit: "Campaign",
        spanFull: true,
        linkTarget: "monitor",
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "start", label: "Start Date", kind: "text", nowrap: true },
            { key: "blocked", label: "Blocked On", kind: "badge", nowrap: true },
        ],
    },
    testFile: {
        title: "Test File Received",
        emptyIcon: "bi-file-earmark-check",
        unit: "Organization",
        columns: [
            { key: "client", label: "Organization", kind: "link" },
            { key: "got", label: "Test File Received?", kind: "badge", nowrap: true },
            { key: "date", label: "Received Date", kind: "text", nowrap: true },
        ],
    },
    goLiveFile: {
        title: "Go-Live File Received",
        emptyIcon: "bi-file-earmark-check",
        tip: "First production file, not a test file",
        unit: "Organization",
        columns: [
            { key: "client", label: "Organization", kind: "link" },
            { key: "got", label: "Go-Live File Received?", kind: "badge", nowrap: true },
            { key: "date", label: "Received Date", kind: "text", nowrap: true },
        ],
    },
    gpOnboarding: {
        title: "GuidePosts Awaiting Approval / In Rework",
        emptyIcon: "bi-file-earmark-richtext",
        tip: "Mirrors the Live tab's content checks during onboarding — GuidePosts still waiting on client approval, and those sent back for rework, before the campaign goes live.",
        unit: "Ad Group",
        spanFull: true,
        columns: [
            { key: "org", label: "Organization", kind: "text" },
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "group", label: "Ad Group", kind: "text" },
            { key: "awaiting", label: "# Awaiting Client Approval", kind: "badge", nowrap: true, align: "right" },
            { key: "rework", label: "# In Rework", kind: "badge", nowrap: true, align: "right" },
        ],
    },
    internalReadiness: {
        title: "Internal Setup Readiness",
        emptyIcon: "bi-list-check",
        tip: "How close the setup is to being completed internally.",
        unit: "Campaign",
        spanFull: true,
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "remaining", label: "# Setup Items Remaining", kind: "progress", nowrap: true },
            { key: "start", label: "Start Date", kind: "text", nowrap: true },
        ],
    },
    clientPending: {
        title: "Client-Pending Items",
        emptyIcon: "bi-hourglass-split",
        tip: "What's still outstanding from the client before launch",
        unit: "Campaign",
        spanFull: true,
        columns: [
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "outstanding", label: "Outstanding From Client", kind: "text" },
            { key: "start", label: "Start Date", kind: "text", nowrap: true },
        ],
    },
    ended: {
        title: "Ended Campaigns",
        emptyIcon: "bi-archive",
        unit: "Campaign",
        spanFull: true,
        columns: [
            { key: "org", label: "Organization", kind: "text" },
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "end", label: "End Date", kind: "text", nowrap: true },
        ],
    },
    noContinuation: {
        title: "Campaigns Ending Without Continuation",
        // NOT the design's wording, which is "no other campaign Live or in Onboarding".
        // That sentence is PRINTED on the card (OpsTileCard renders `tip` as the card
        // description, not only inside the info tooltip), and the rule is stricter than
        // it in two ways that both fire on this table's headline cases: a campaign whose
        // own flight also ends inside the same 14 days is not continuation, and neither
        // is an onboarding campaign with no start date on its schedule. An organization
        // can therefore be flagged while plainly having "another campaign Live" — which
        // reads as the table being broken. "Nothing else scheduled to keep serving past
        // them" covers both and is the ticket's own phrase for what makes a gap.
        tip: "Campaigns ending in the next 14 days where the organization has nothing else scheduled to keep serving past them. Plan the next launch before delivery stops.",
        // What is absent is the NEXT campaign, so the icon pictures an empty calendar
        // rather than an archive box — nothing has ended here yet, which is the point.
        emptyIcon: "bi-calendar-x",
        unit: "Campaign",
        spanFull: true,
        columns: [
            // The design links Organization as well as Campaign, but a row resolves ONE
            // destination from its `subject_type` (see rowLink.ts), and this row's
            // subject is the campaign. Two anchors pointing at the same campaign page
            // would read as two different destinations. Campaign keeps the link, as it
            // does on Ended Campaigns, which lays out the same two columns.
            { key: "org", label: "Organization", kind: "text" },
            { key: "campaign", label: "Campaign", kind: "link" },
            { key: "end", label: "Campaign End Date", kind: "text", nowrap: true },
            { key: "contract_end", label: "Contract End Date", kind: "text", nowrap: true },
        ],
    },
};

export interface MarginTrendPoint {
    label: string;
    pct: number;
}

/** Margin Tracking's stat tiles and trend — see MarginSummaryOut. */
export interface MarginSummary {
    /** Null where no campaign has a revenue figure to divide by. Not 0. */
    blended_pct: number | null;
    at_or_above: number;
    below: number;
    target_pct: number;
    weekly: MarginTrendPoint[];
    monthly: MarginTrendPoint[];
}

export interface TileGroup {
    key: string;
    id: string;
    label: string;
    /**
     * "grid" = a responsive card grid. "single" = one full-width table in its own
     * section with no grid, which is how the mockup lays out List Size, Live Campaigns
     * and Margin Tracking.
     */
    layout: "grid" | "single";
    /** Heading text where it differs from the jump-bar label, per the mockup. */
    heading?: string;
    description?: string;
    fixed?: boolean;
    /**
     * Renders as a titled PANEL wrapping its tables rather than a page section: a
     * sentence-case heading, its description, and the cards inside one container — no
     * collapse control, no accent rule, no item count. File Management is drawn this way
     * in the design because it groups two halves of one question, where Triage and the
     * rest are divisions of the page itself.
     */
    panel?: boolean;
    /**
     * A dashed slot rendered after the group's tables, naming reporting that is coming
     * but not yet specified. MPN-1867 asks for one on Ended — "build the campaign list
     * and leave a visible placeholder for the reporting that will follow" — so the tab
     * reads as deliberately unfinished rather than as a tab somebody forgot.
     *
     * Deliberately not a card: an empty card looks like a table that failed to load.
     */
    placeholder?: string;
    /**
     * Renders a named component above the group's tables. Margin Tracking needs stat
     * tiles and a chart there, and the registry only models tables.
     */
    lead?: "marginTracking";
    tiles: string[];
}

/** Groups per lifecycle tab, from the mockup's groupDefs() and panel markup. */
export const TAB_GROUPS: Record<OpsTab, TileGroup[]> = {
    live: [
        {
            key: "triage", id: "grp-triage", label: "Triage",
            heading: "Featured — daily triage",
            fixed: true, layout: "grid",
            tiles: ["overdueFiles", "adsScheduling", "audienceChanges", "conversionsFlat", "constituents", "offboard"],
        },
        {
            key: "listSize", id: "grp-listsize", label: "List Size", fixed: true, layout: "single",
            heading: "List Size Management",
            description: "Tracked at the contract level, not per-campaign — a contract's max list size rolls up across every live campaign under it. The Organization shown is the contract holder, which may be a parent org above the campaign-running sub-orgs.",
            tiles: ["listSize"],
        },
        {
            key: "content", id: "grp-content", label: "Content & Trafficking", layout: "grid",
            tiles: ["scheduledNotServing", "servingNotScheduled", "approval", "reworks"],
        },
        {
            key: "budget", id: "grp-budget", label: "Budget & Performance", layout: "grid",
            // lowMatchRate sits directly below lowReach, as MPN-2074 item 10 places it.
            tiles: ["pfrBudget", "underdelivery", "lowReach", "lowMatchRate", "overspend", "cpcCpm"],
        },
        {
            key: "monitoring", id: "grp-monitoring", label: "Monitoring & Follow-Up", layout: "grid",
            tiles: ["spendDropoff", "creativeRefresh", "reviewCadence", "keyDates"],
        },
        {
            key: "liveCampaigns", id: "grp-livecampaigns", label: "Live Campaigns", layout: "single",
            tiles: ["liveCampaigns"],
        },
        {
            key: "margin", id: "grp-margin", label: "Margin Tracking", layout: "single",
            lead: "marginTracking",
            tiles: ["marginByCampaign"],
        },
    ],
    onboarding: [
        {
            key: "onboardTriage", id: "grp-onboard-triage", label: "Triage",
            heading: "Featured — daily triage",
            description: "Onboarding-specific daily-need items.",
            fixed: true, layout: "grid",
            tiles: ["readiness"],
        },
        {
            // MPN-1863 places only the two file tables here.
            key: "files", id: "grp-files", label: "File Management", panel: true, layout: "grid",
            description: "Onboarding-specific checks — confirming test and go-live files have actually been received before a campaign goes live.",
            tiles: ["testFile", "goLiveFile"],
        },
        {
            // MPN-1864 puts GuidePosts Awaiting Approval / In Rework in its own Content section.
            key: "onboardContent", id: "grp-onboard-content", label: "Content", layout: "grid",
            tiles: ["gpOnboarding"],
        },
        {
            // MPN-1865 and MPN-1866 both name this section "Setup Readiness".
            key: "setupReadiness", id: "grp-setup-readiness", label: "Setup Readiness", layout: "grid",
            tiles: ["internalReadiness", "clientPending"],
        },
    ],
    ended: [
        {
            key: "endedCampaigns", id: "grp-ended", label: "Ended Campaigns",
            heading: "Ended campaigns",
            // MPN-2074: the tab is launch lifecycle, not engagement status, so it also
            // holds campaigns whose flight is over on an engagement still open. One of
            // those can appear on the Live tab's Offboard Dates table at the same time —
            // it has stopped serving and nobody has offboarded it, which are two true
            // things about the same campaign.
            description: "Campaigns whose engagement has closed, or whose flight is already over. The end date comes from the schedule — campaigns closed without one show a dash and fall outside both end-date windows.",
            fixed: true, layout: "single",
            placeholder: "Ended-specific reporting is still to be defined. Final margin and a historical performance summary were raised as candidates; neither is specified yet, so nothing is shown rather than a figure nobody has agreed.",
            tiles: ["ended"],
        },
    ],
    client: [
        {
            // Drawn as a PANEL, which is how the design draws it: a sentence-case
            // heading, its explanation, and the table inside one container. Triage and
            // the rest are divisions of the page itself and take a section heading with
            // an accent rule; this tab holds one table and the heading is explaining why
            // the tab exists at all.
            key: "clientRelationship", id: "grp-client", label: "Client Management",
            heading: "Client relationship management",
            description: "For CSMs. Onboarding, Live and Ended are Ops-facing and organised by campaign status. This tab answers a different question for a different person \u2014 where a client relationship is ongoing but delivery is about to stop \u2014 so it sits outside the status structure.",
            panel: true, fixed: true, layout: "single",
            tiles: ["noContinuation"],
        },
    ],
};

/** The mockup's label(n, unit): "1 Campaign", "7 Campaigns", "5 GuidePosts". */
export function unitLabel(n: number, unit: string): string {
    if (n === 1) return `1 ${unit}`;
    return `${n} ${plural(unit)}`;
}

/** Always plural, for the "4 of 6 Campaigns" form where the leading number is not the count. */
export function plural(unit: string): string {
    return unit === "GuidePost" ? "GuidePosts" : `${unit}s`;
}

/** Whether the filter bar is narrowing the page. Decides which count form a card shows. */
export function filtersActive(filters: OpsFilters): boolean {
    return (
        filters.opsOwnerIds.length > 0
        || filters.organizationIds.length > 0
        || filters.csmIds.length > 0
    );
}
